import React, {useContext, useEffect, useState} from "react";
import {Row} from "antd";
import {ArrowsAltOutlined, ShrinkOutlined} from "@ant-design/icons";
import {MapContainer, MapTools} from "./DeliveryRequestMap.styles";
import {useTranslation} from "react-i18next";
import {CreateDeliveryContext} from "../CreateDeliveryRequest";
import {LeafletMapBase} from "../../_defaultComponents/LeafletMapBase/LeafletMapBase";
import {DeliveryRequestMarker} from "../../_defaultComponents/DeliveryRequestMarker/DeliveryRequestMarker";
import {DeliveryRequestPolyline} from "../../_defaultComponents/DeliveryRequestPolyline/DeliveryRequestPolyline";

export const DeliveryRequestMap = () => {
    const {i18n} = useTranslation([]);
    const defaultCenter = {lat: 41.0, lng: 29};
    const defaultZoom = 10;
    const [mapInstance, setMapInstance] = useState(null);
    const [mapMode, setMapMode] = useState("relative");
    const [center, setCenter] = useState(defaultCenter);
    const [zoom, setZoom] = useState(defaultZoom);

    const {
        markerList = [],
        orderList = [],
        updateOrder,
        reverseGeocode,
        handleMapClick,
        polylineData,
        polylineLoading
    } = useContext(CreateDeliveryContext);

    const toggleMapMode = e => {
        if (mapMode == "fixed") {
            setMapMode("relative");
        } else {
            setMapMode("fixed");
        }
        e.currentTarget.blur();
    };

    const fitBounds = locations => {
        if (!mapInstance || !locations || locations.length === 0) return;

        const bounds = locations.reduce((acc, location) => {
            if (location?.latitude && location?.longitude) {
                acc.push([location.latitude, location.longitude]);
            }
            return acc;
        }, []);

        if (bounds.length === 0) return;

        mapInstance.fitBounds(bounds);
        if (bounds.length === 1) {
            mapInstance.setZoom(mapInstance.getZoom() - 5);
        }
    };

    const handleEsc = event => {
        if (event.keyCode === 27 && mapMode == "fixed") {
            setMapMode("relative");
        }
    };

    const onMapReady = map => {
        setMapInstance(map);
        fitBounds(markerList.map(marker => marker.location));
    };

    const handleMarkerDragEnd = async (lat, lng, markerData) => {
        const isDeliveryPoint = markerData.color === "gray";
        if (!isDeliveryPoint) return;

        // Find the order index from marker title (title format: "3", "4", etc.)
        const orderIndex = parseInt(markerData.title) - 2; // -2 because pickup is 1, orders start from 2

        if (orderIndex >= 0 && reverseGeocode) {
            const targetOrder = orderList?.[orderIndex];
            const preservedFields = {};
            if (targetOrder?.receiverName) {
                preservedFields.receiverName = targetOrder.receiverName;
            }
            if (targetOrder?.receiptNumber) {
                preservedFields.receiptNumber = targetOrder.receiptNumber;
            }
            if (targetOrder?.receiverPhone) {
                preservedFields.receiverPhone = targetOrder.receiverPhone;
            }
            try {
                const geocodeResult = await reverseGeocode(lat, lng);
                updateOrder({
                    orderIndex: orderIndex,
                    order: {
                        ...preservedFields,
                        receiverLocation: {lat, lon: lng},
                        receiverAddress: geocodeResult?.address,
                        receiverCity: geocodeResult?.city || targetOrder?.receiverCity || ""
                    }
                });
            } catch (error) {
                updateOrder({
                    orderIndex: orderIndex,
                    order: {
                        ...preservedFields,
                        receiverLocation: {lat, lon: lng}
                    }
                });
            }
        }
    };

    // Fit bounds when marker list changes
    useEffect(() => {
        if (mapInstance && markerList.length > 0) {
            fitBounds(markerList.map(marker => marker.location));
        }
    }, [markerList, mapInstance]);

    useEffect(() => {
        window.addEventListener("keydown", handleEsc);
        return () => {
            window.removeEventListener("keydown", handleEsc);
        };
    }, [mapMode]);

    useEffect(() => {
        if (!mapInstance) return;

        const resizeMap = () => {
            mapInstance.invalidateSize();
            if (markerList?.length) {
                fitBounds(markerList.map(marker => marker.location));
            }
        };

        // wait for layout transition
        const timeoutId = setTimeout(resizeMap, 150);

        return () => clearTimeout(timeoutId);
    }, [mapMode, mapInstance, markerList]);

    return (
        <Row gutter={[0, 30]} style={{height: "100%"}}>
            <MapContainer mode={mapMode} data-testid="map">
                <MapTools onClick={toggleMapMode}>
                    {mapMode == "relative" ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
                </MapTools>
                {polylineLoading && (
                    <div
                        style={{
                            position: "absolute",
                            top: "50px",
                            left: "10px",
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            padding: "8px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            zIndex: 1000
                        }}
                    >
                        Rota yükleniyor...
                    </div>
                )}
                <LeafletMapBase
                    center={center}
                    zoom={zoom}
                    onMapReady={onMapReady}
                    onMapClick={handleMapClick}
                    style={{width: "100%", height: "100%"}}
                    tileLayerControlStyle={{
                        position: "absolute",
                        top: "10px",
                        right: "50px",
                        zIndex: 1000
                    }}
                >
                    {/* Render markers */}
                    {markerList.map((markerData, index) => (
                        <DeliveryRequestMarker
                            key={`marker-${index}`}
                            mapInstance={mapInstance}
                            markerData={markerData}
                            onDragEnd={handleMarkerDragEnd}
                            isVisible={true}
                        />
                    ))}

                    {/* Render polyline */}
                    <DeliveryRequestPolyline
                        mapInstance={mapInstance}
                        polylineData={polylineData}
                        isVisible={!polylineLoading}
                    />
                </LeafletMapBase>
            </MapContainer>
        </Row>
    );
};
